import { consola } from 'consola'

const logger = consola.withTag('analytics:session')

const SESSION_KEY = 'analytics_session'
const ATTRIBUTION_KEY = 'analytics_attribution'
const UTM_KEY = 'analytics_utm'

/** Сессия рвётся после получаса без событий — как в Метрике, чтобы отчёты сходились. */
const SESSION_IDLE_MS = 30 * 60 * 1000

/** Сколько живёт атрибуция пуша и deeplink: по своей рассылке заказывают в тот же день. */
const ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Окно атрибуции рекламы — длиннее: между «увидел баннер» и «заказал» проходят дни.
 * 30 суток — отраслевой дефолт; правится здесь одним числом.
 */
const UTM_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

/** Метки рекламной кампании из адреса страницы. */
export interface UtmParams {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
}

export interface AnalyticsAttribution extends UtmParams {
  /** Откуда пришёл пользователь: push | deeplink | referral | utm. */
  source: string
  /** UUID уведомления, если вход был из пуша, — сшивает воронку с рассылкой. */
  notificationId?: string
  campaign?: string
  /** Момент фиксации, миллисекунды. */
  ts: number
}

/** Как часто состояние сессии реально пишется в localStorage. */
const SESSION_PERSIST_THROTTLE_MS = 60 * 1000

interface StoredSession {
  id: string
  /** Время последнего события — по нему решаем, жива ли сессия. */
  lastActivityAt: number
}

let cachedSession: StoredSession | null = null
let lastPersistedAt = 0

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // Старые WebView без randomUUID: формат UUID v4 важен — колонки в БД типизированы.
  return '10000000-1000-4000-8000-100000000000'.replaceAll(/[018]/g, (c) => {
    const n = Number(c)
    return (n ^ (Math.trunc(Math.random() * 256) & (15 >> (n / 4)))).toString(16)
  })
}

const readJson = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const writeJson = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    // Приватный режим / переполненное хранилище — аналитика не имеет права падать.
    logger.debug('Не удалось сохранить состояние аналитики', error)
  }
}

/**
 * Возвращает идентификатор текущей сессии, продлевая её. Тот же идентификатор
 * уезжает на бэк заголовком `X-Analytics-Session`, чтобы серверные события
 * (обмен токенов авторизации, webhook оплаты) склеились с клиентской воронкой.
 */
export const getAnalyticsSessionId = (): string => {
  if (!process.client) return ''

  const now = Date.now()
  // Читаем из памяти, а не из localStorage: функция дёргается на каждый API-запрос,
  // а JSON.parse + запись на каждый запрос — заметная синхронная работа в главном потоке.
  const stored = cachedSession ?? readJson<StoredSession>(SESSION_KEY)

  const isAlive = stored?.id && now - stored.lastActivityAt < SESSION_IDLE_MS
  const session: StoredSession = isAlive
    ? { id: stored!.id, lastActivityAt: now }
    : { id: createId(), lastActivityAt: now }

  cachedSession = session

  // В хранилище сбрасываем раз в минуту: точность «когда была последняя активность»
  // до секунды не нужна, а порог протухания сессии — полчаса.
  if (!isAlive || now - lastPersistedAt > SESSION_PERSIST_THROTTLE_MS) {
    lastPersistedAt = now
    writeJson(SESSION_KEY, session)
  }

  return session.id
}

/** Новый идентификатор события. Он же ключ идемпотентности при ретраях отправки. */
export const createAnalyticsEventId = (): string => createId()

const removeKey = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    // Нечего чистить — состояние и так недоступно.
  }
}

/** Метки рекламы с моментом фиксации — отдельный слот, у него своё окно жизни. */
type StoredUtm = UtmParams & { ts: number }

/**
 * Ранние версии писали UTM в общий слот атрибуции, и пуш затирал рекламу: заказ
 * «увидел баннер → через неделю открыл пуш → заказал» уезжал в органику. Найденный
 * старый формат переезжает в свой слот, чтобы окно 30 суток не потерялось.
 */
const migrateLegacyUtm = (): void => {
  const legacy = readJson<AnalyticsAttribution>(ATTRIBUTION_KEY)
  if (legacy?.source !== 'utm') return

  if (legacy.utmSource && !readJson<StoredUtm>(UTM_KEY)) {
    const { utmSource, utmMedium, utmCampaign, utmContent, utmTerm, ts } = legacy
    writeJson(UTM_KEY, { utmSource, utmMedium, utmCampaign, utmContent, utmTerm, ts })
  }
  removeKey(ATTRIBUTION_KEY)
}

/**
 * Фиксирует источник входа (push, deeplink, referral). Прилипает ко всем последующим
 * событиям сессии, поэтому по пушу видно не «открываемость», а доведённые до оплаты
 * заказы. Рекламные метки живут в СВОЁМ слоте ({@link setUtmAttribution}) — вход из
 * пуша их не затирает, иначе заказ по рекламе уезжал бы в органику.
 */
export const setAnalyticsAttribution = (attribution: Omit<AnalyticsAttribution, 'ts'>): void => {
  if (!process.client) return
  writeJson(ATTRIBUTION_KEY, { ...attribution, ts: Date.now() })
}

/** Фиксирует рекламные метки. Прошлые затираются целиком: считаем по последнему клику. */
export const setUtmAttribution = (utm: UtmParams): void => {
  if (!process.client) return
  writeJson(UTM_KEY, { ...utm, ts: Date.now() })
}

/** Живые рекламные метки (окно 30 суток) или null. */
export const getUtmAttribution = (): StoredUtm | null => {
  if (!process.client) return null

  migrateLegacyUtm()

  const stored = readJson<StoredUtm>(UTM_KEY)
  if (!stored?.utmSource) return null

  if (Date.now() - stored.ts > UTM_ATTRIBUTION_TTL_MS) {
    removeKey(UTM_KEY)
    return null
  }

  return stored
}

/**
 * Сводная атрибуция для событий: источник входа — по последнему клику из двух слотов,
 * рекламные метки — всегда, пока живо их окно. Так пуш остаётся источником сессии,
 * но не отнимает у рекламы заказ, оплаченный внутри её 30 суток.
 */
export const getAnalyticsAttribution = (): AnalyticsAttribution | null => {
  if (!process.client) return null

  migrateLegacyUtm()

  const utm = getUtmAttribution()

  const entry = readJson<AnalyticsAttribution>(ATTRIBUTION_KEY)
  const entryAlive = entry && Date.now() - entry.ts <= ATTRIBUTION_TTL_MS
  if (entry && !entryAlive) removeKey(ATTRIBUTION_KEY)

  if (!entryAlive && !utm) return null

  const utmFields: UtmParams = utm
    ? {
        utmSource: utm.utmSource,
        utmMedium: utm.utmMedium,
        utmCampaign: utm.utmCampaign,
        utmContent: utm.utmContent,
        utmTerm: utm.utmTerm,
      }
    : {}

  // Свежий вход из пуша главнее как ИСТОЧНИК сессии, но метки рекламы едут рядом.
  // При равном времени побеждает реклама: захват меток идёт первым делом на старте.
  if (entryAlive && (!utm || entry!.ts > utm.ts)) {
    return { ...entry!, ...utmFields }
  }

  return {
    source: 'utm',
    // Дублируем в campaign: у пуша это идентификатор рассылки, у рекламы — кампании,
    // и отчётам удобно читать одно поле независимо от канала.
    campaign: utm!.utmCampaign,
    ...utmFields,
    ts: utm!.ts,
  }
}

/** Отпечаток устройства считается асинхронно — здесь лежит последний известный. */
let deviceId: string | undefined

export const setAnalyticsDeviceId = (value: string | undefined): void => {
  deviceId = value
}

export const getAnalyticsDeviceId = (): string | undefined => deviceId
